import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { notifications } from '@mantine/notifications';
import { useCreateSection, useUpdateSection } from '../../hooks/useSections';
import type { CreateSectionDto, Section, UpdateSectionDto } from '../../types';
import { sectionFormSchema } from '../../utils/sectionValidation';
import { Button, ControlledInput, ControlledFileInput } from '@shared/ui';
import './SectionForm.css';

interface SectionFormProps {
  section?: Section;
  onSuccess?: () => void;
}

type FileInputValue = File | { id: number; url: string };

interface SectionFormData extends CreateSectionDto {
  image?: FileInputValue | null;
}

export default function SectionForm({ section, onSuccess }: SectionFormProps) {
  const isEditMode = !!section;
  const resolver = yupResolver(sectionFormSchema) as any;

  const { handleSubmit, formState: { errors }, reset, control } = useForm<SectionFormData>({
    resolver,
    context: { requireImage: !isEditMode },
    defaultValues: {
      name: section?.name || '',
      image: section?.imageUrl ? { id: 0, url: section.imageUrl } : null,
    },
  });
  const createSection = useCreateSection();
  const updateSection = useUpdateSection();

  useEffect(() => {
    if (section) {
      reset({
        name: section.name,
        image: section.imageUrl ? { id: 0, url: section.imageUrl } : null,
      });
    }
  }, [section, reset]);

  const onSubmit = async (data: SectionFormData) => {
    try {
      const imageFile = data.image instanceof File ? data.image : undefined;

      if (isEditMode && section) {
        const updateData: UpdateSectionDto = {
          name: data.name,
        };

        // Определяем, было ли изображение удалено
        // Если изначально было изображение, а сейчас null - значит удалено
        const hadImage = section.imageUrl && section.imageUrl.length > 0;
        const imageRemoved = hadImage && data.image === null;

        if (imageRemoved) {
          updateData.shouldDeleteImage = true;
        }

        await updateSection.mutateAsync({
          id: section.id,
          data: updateData,
          file: imageFile
        });
        notifications.show({
          title: 'Успешно',
          message: 'Секция успешно обновлена',
          color: 'green',
        });
      } else {
        const createData: CreateSectionDto = {
          name: data.name,
        };
        await createSection.mutateAsync({ data: createData, file: imageFile });
        notifications.show({
          title: 'Успешно',
          message: 'Секция успешно создана',
          color: 'green',
        });
      }
      reset();
      onSuccess?.();
    } catch (error) {
      notifications.show({
        title: 'Ошибка',
        message: `Ошибка при ${isEditMode ? 'обновлении' : 'создании'} секции`,
        color: 'red',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="section-form">
      <div className="section-form__columns">
        <div className="section-form__left">
          <div className="form-group">
            <ControlledInput
              id="name"
              type="text"
              label="Название *"
              name="name"
              control={control}
              error={errors.name}
            />
          </div>
        </div>

        <div className="section-form__right">
          <div className="form-group section-form__image">
            <ControlledFileInput
              id="image"
              name="image"
              label={`Изображение ${!isEditMode ? '*' : ''}`}
              description="Загружайте квадратные фото в формате 1:1 — так они корректно отображаются в каталоге."
              accept="image/*"
              control={control}
              error={errors.image}
            />
          </div>
        </div>
      </div>

      <Button
        type="submit"
        loading={createSection.isPending || updateSection.isPending}
        disabled={createSection.isPending || updateSection.isPending}
        className="section-form__submit-button"
      >
        {isEditMode ? 'Обновить секцию' : 'Создать секцию'}
      </Button>
    </form>
  );
}

